// src/routes/AppRoutes.tsx
import React, { ReactNode, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';

import LandingPage from '../pages/LandingPage';
import ServicesPage from '../pages/ServicesPage';
import PricingPage from '../pages/PricingPage';
import CareersPage from '../pages/CareersPage';
import CareerJobPage from '../pages/CareerJobPage';
import CareerApplicationThankYouPage from '../pages/CareerApplicationThankYouPage';
import CareerInterviewSchedulerPage from '../pages/CareerInterviewSchedulerPage';
import InsightsV2Page from '../pages/InsightsV2Page';
import InsightV2ArticlePage from '../pages/InsightV2ArticlePage';
import CapitalPartnerNetworkPage from '../pages/CapitalPartnerNetworkPage';
import WorksPage from '../pages/WorksPage';
import CaseStudyPage from '../pages/CaseStudyPage';
import ServiceSelectionPage from '../pages/funnel/ServiceSelectionPage';
import CheckoutPage from '../pages/funnel/CheckoutPage';
import CheckoutSuccessPage from '../pages/funnel/CheckoutSuccessPage';
import CheckoutCancelPage from '../pages/funnel/CheckoutCancelPage';
import AuthActionPage from '../pages/AuthActionPage';
import PlatformInvitePage from '../pages/PlatformInvitePage';

import UnifiedDashboard from '../pages/UnifiedDashboard';

// Email verification handler
import VerifyEmailPage from '../pages/VerifyEmail';
import AccountLockedPage from '../pages/AccountLockedPage';

// Client pages (new IA)
import ClientHomePage from '../pages/client/ClientHomePage';
import ClientBillingPage from '../pages/client/ClientBillingPage';
import ClientServicesPage from '../pages/client/ClientServicesPage';
import ClientTeamsPage from '../pages/client/ClientTeamsPage';
import AssetManagerPage from '../pages/client/AssetManagerPage';
import ClientProjectsPage from '../pages/client/ClientProjectsPage';
import JoinTeamPage from '../pages/JoinTeamPage';

// Admin pages
import OverviewPage from '../pages/admin/OverviewPage';
import AppsPage from '../pages/admin/AppsPage';
import PublicationsPage from '../pages/admin/PublicationsPage';
import UsersPage from '../pages/admin/UsersPage';
import LeadsPage from '../pages/admin/LeadsPage';
import JobBoardPage from '../pages/admin/JobBoardPage';
import OrganizationPage from '../pages/admin/OrganizationPage';
import SlaSetupPage from '../pages/admin/SlaSetupPage';

// Agent/shared pages
import TicketQueuePage from '../pages/shared/TicketQueuePage';
import AgentTicketsPage from '../pages/shared/AgentTicketsPage';
import AgentTicketsBoardPage from '../pages/shared/AgentTicketsBoardPage';
import TeamsOverviewPage from '../pages/shared/tickets/teams/TeamsOverviewPage';
import TeamsKnowledgeBasePage from '../pages/shared/tickets/teams/TeamsKnowledgeBasePage';
import TeamsOrganizationsPage from '../pages/shared/tickets/teams/TeamsOrganizationsPage';
import TeamsProjectsPage from '../pages/shared/tickets/teams/TeamsProjectsPage';
import TeamsQueuePage from '../pages/shared/tickets/teams/TeamsQueuePage';

// Shared
import ProfilePage from '../pages/shared/ProfilePage';
import UserProfilePage from '../pages/shared/UserProfilePage';

import { useAuth } from '../context/AuthProvider';

/* ────────── home redirect ────────── */
function HomeRedirect() {
  const { role, loading, isBanned } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (loading) return;

    if (isBanned) {
      nav('/account-locked', { replace: true });
      return;
    }

    // Authenticated users:
    // - admin/agent continue to dashboard by default
    // - client is allowed to stay on public routes (e.g., clicking logo to "/")
    if (role === 'admin' || role === 'agent') {
      nav('/dashboard', { replace: true });
    } else if (role === 'candidate') {
      nav('/careers', { replace: true });
    }
    // no role → stay on landing page
  }, [loading, role, nav, isBanned]);

  if (loading) return <div style={{ padding: '2rem' }}>Loading…</div>;
  return <LandingPage />;
}

/* ────────── route guards ────────── */
type UserRole = 'admin' | 'agent' | 'client' | 'candidate';

function RoleRoute({ roles, children }: { roles: UserRole[]; children: ReactNode }) {
  const { role, loading, isBanned } = useAuth();
  if (loading) return <div style={{ padding: '2rem' }}>Loading…</div>;
  if (isBanned) return <Navigate to="/account-locked" replace />;
  return role && roles.includes(role as UserRole) ? <>{children}</> : <Navigate to="/dashboard" replace />;
}

function CreateTicketRedirect() {
  const { role } = useAuth();
  if (role === 'admin' || role === 'agent') {
    return <Navigate to="/dashboard/teams/projects" replace />;
  }
  return <Navigate to="/dashboard/projects" replace />;
}

function AuthRoute({ children }: { children: ReactNode }) {
  const { user, loading, isBanned } = useAuth();
  if (loading) return <div style={{ padding: '2rem' }}>Loading…</div>;
  if (isBanned) return <Navigate to="/account-locked" replace />;
  if (!user) return <Navigate to="/" replace />;

  return <>{children}</>;
}

// Only use for things that truly require payment (e.g., create ticket)
function PaidRoute({ children }: { children: ReactNode }) {
  const { role, isPaid, loading } = useAuth();
  if (loading) return <div style={{ padding: '2rem' }}>Loading…</div>;
  if (role === 'client' && !isPaid) return <Navigate to="/dashboard/billing" replace />;
  return <>{children}</>;
}

/* ────────── routes map ────────── */
function ClientWorkspaceRoute({ children }: { children: ReactNode }) {
  const { role, isPrimaryAccountHolder, hasOrganizationMembership, loading } = useAuth();
  if (loading) return <div style={{ padding: '2rem' }}>Loading...</div>;
  if (role === 'client' && !isPrimaryAccountHolder && !hasOrganizationMembership) {
    return <Navigate to="/dashboard/profile" replace />;
  }
  return <>{children}</>;
}

export default function AppRoutes() {
  return (
    <Routes>
      {/* smart landing */}
      <Route path="/" element={<HomeRedirect />} />
      <Route path="/account-locked" element={<AccountLockedPage />} />

      {/* public marketing */}
      <Route path="/services" element={<ServicesPage />} />
      <Route path="/insights-v2" element={<Navigate to="/publications" replace />} />
      <Route path="/insights-v2/:slug" element={<LegacyInsightSlugRedirect />} />
      <Route path="/publications" element={<InsightsV2Page />} />
      <Route path="/publications/:slug" element={<InsightV2ArticlePage />} />
      <Route path="/blogs" element={<Navigate to="/publications" replace />} />
      <Route path="/blogs/:slug" element={<LegacyInsightSlugRedirect />} />
      <Route path="/capital-partner-network" element={<CapitalPartnerNetworkPage />} />
      <Route path="/blog/:slug" element={<LegacyInsightSlugRedirect />} />
      <Route path="/works" element={<WorksPage />} />
      <Route path="/works/:slug" element={<CaseStudyPage />} />
      <Route path="/case-studies" element={<Navigate to="/works" replace />} />
      <Route path="/case-studies/:slug" element={<CaseStudyPage />} />
      <Route path="/case-study/:slug" element={<CaseStudyPage />} />
      <Route path="/services/:serviceSlug" element={<ServiceSelectionPage />} />
      <Route path="/careers" element={<CareersPage />} />
      <Route path="/careers/interview/:inviteToken" element={<CareerInterviewSchedulerPage />} />
      <Route path="/careers/:jobSlug" element={<CareerJobPage />} />
      <Route path="/careers/:jobSlug/thank-you" element={<CareerApplicationThankYouPage />} />
      <Route path="/checkout" element={<CheckoutPage />} />
      <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
      <Route path="/checkout/cancel" element={<CheckoutCancelPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/auth/action" element={<AuthActionPage />} />
      <Route path="/invite/platform" element={<PlatformInvitePage />} />
      <Route path="/start" element={<Navigate to="/dashboard" replace />} />

      {/* email verification */}
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/join" element={<JoinTeamPage />} />
      <Route path="/lead" element={<Navigate to="/" replace />} />

      {/* unified dashboard for all authenticated users */}
       <Route
        path="/dashboard"
        element={
          <AuthRoute>
            <UnifiedDashboard />
          </AuthRoute>
        }
      >
        {/* Client defaults (always accessible) */}
        <Route path="home" element={<RoleRoute roles={['client']}><ClientWorkspaceRoute><ClientHomePage /></ClientWorkspaceRoute></RoleRoute>} />
        <Route path="services" element={<RoleRoute roles={['client']}><ClientWorkspaceRoute><ClientServicesPage /></ClientWorkspaceRoute></RoleRoute>} />
        <Route path="billing" element={<RoleRoute roles={['client']}><ClientWorkspaceRoute><ClientBillingPage /></ClientWorkspaceRoute></RoleRoute>} />
        <Route path="subscriptions" element={<RoleRoute roles={['client']}><Navigate to="/dashboard/services" replace /></RoleRoute>} />
        <Route path="business" element={<RoleRoute roles={['client']}><ClientWorkspaceRoute><ClientTeamsPage /></ClientWorkspaceRoute></RoleRoute>} />

        {/* Require payment for these client areas */}
        <Route
          path="assets"
          element={
            <RoleRoute roles={['client']}>
              <ClientWorkspaceRoute>
                <PaidRoute>
                  <AssetManagerPage />
                </PaidRoute>
              </ClientWorkspaceRoute>
            </RoleRoute>
          }
        />
        <Route
          path="projects"
          element={
            <RoleRoute roles={['client']}>
              <ClientProjectsPage />
            </RoleRoute>
          }
        />
        <Route
          path="tickets"
          element={
            <RoleRoute roles={['client']}>
              <Navigate to="/dashboard/projects" replace />
            </RoleRoute>
          }
        />
        <Route
          path="teams"
          element={
            <RoleRoute roles={['client']}>
              <Navigate to="/dashboard/business" replace />
            </RoleRoute>
          }
        />
        <Route path="business-settings" element={<Navigate to="/dashboard/business" replace />} />
        <Route path="teams/settings" element={<Navigate to="/dashboard/business" replace />} />

        {/* Create ticket stays hard‑gated as before */}
        <Route
          path="tickets/new"
          element={
            <RoleRoute roles={['admin', 'agent', 'client']}>
              <ClientWorkspaceRoute>
                <PaidRoute>
                  <CreateTicketRedirect />
                </PaidRoute>
              </ClientWorkspaceRoute>
            </RoleRoute>
          }
        />

        {/* Admin-only */}
        <Route path="overview" element={<RoleRoute roles={['admin', 'agent']}><OverviewPage /></RoleRoute>} />
        <Route path="apps" element={<RoleRoute roles={['admin', 'agent']}><AppsPage /></RoleRoute>} />
        <Route path="publications" element={<RoleRoute roles={['admin', 'agent']}><PublicationsPage /></RoleRoute>} />
        <Route path="users" element={<RoleRoute roles={['admin']}><UsersPage /></RoleRoute>} />
        <Route path="blogs" element={<Navigate to="/dashboard/publications" replace />} />
        <Route path="leads" element={<RoleRoute roles={['admin', 'agent']}><LeadsPage /></RoleRoute>} />
        <Route path="admin/organizations" element={<Navigate to="/dashboard/teams/organizations" replace />} />
        <Route path="job-board" element={<RoleRoute roles={['admin', 'agent']}><JobBoardPage /></RoleRoute>} />
        <Route path="admin/services" element={<RoleRoute roles={['admin']}><SlaSetupPage /></RoleRoute>} />
        <Route path="admin/sla-setup" element={<Navigate to="/dashboard/admin/services" replace />} />
        <Route path="my-team" element={<Navigate to="/dashboard/teams/overview" replace />} />
        <Route path="organization" element={<RoleRoute roles={['admin']}><OrganizationPage /></RoleRoute>} />

        {/* Agent/shared */}
        <Route path="ticket-queue" element={<RoleRoute roles={['admin', 'agent']}><TicketQueuePage /></RoleRoute>} />
        <Route path="list" element={<RoleRoute roles={['admin', 'agent']}><AgentTicketsPage /></RoleRoute>} />
        <Route path="board" element={<RoleRoute roles={['admin', 'agent']}><AgentTicketsBoardPage /></RoleRoute>} />
        <Route path="kbase" element={<RoleRoute roles={['admin', 'agent']}><TeamsKnowledgeBasePage /></RoleRoute>} />
        <Route path="teams/overview" element={<RoleRoute roles={['admin', 'agent']}><TeamsOverviewPage /></RoleRoute>} />
        <Route path="teams/kbase" element={<Navigate to="/dashboard/kbase" replace />} />
        <Route path="teams/organizations" element={<RoleRoute roles={['admin', 'agent']}><TeamsOrganizationsPage /></RoleRoute>} />
        <Route path="teams/projects" element={<RoleRoute roles={['admin', 'agent']}><TeamsProjectsPage /></RoleRoute>} />
        <Route path="teams/queue" element={<RoleRoute roles={['admin', 'agent']}><TeamsQueuePage /></RoleRoute>} />
        <Route path="my-tickets" element={<Navigate to="/dashboard/list" replace />} />

        {/* Shared */}
        <Route path="profile" element={<RoleRoute roles={['admin', 'agent', 'client']}><ProfilePage /></RoleRoute>} />
        <Route
          path="users/profile/:userRef"
          element={
            <RoleRoute roles={['admin', 'agent']}>
              <UserProfilePage />
            </RoleRoute>
          }
        />

        {/* Default redirect based on role */}
        <Route index element={<DashboardRedirect />} />
      </Route>

      {/* catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/* ────────── dashboard redirect based on role ────────── */
function LegacyInsightSlugRedirect() {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={slug ? `/publications/${slug}` : '/publications'} replace />;
}

function DashboardRedirect() {
  const { role, isBanned, isPrimaryAccountHolder, hasOrganizationMembership } = useAuth();
  if (isBanned) return <Navigate to="/account-locked" replace />;
  if (role === 'admin') return <Navigate to="/dashboard/overview" replace />;
  if (role === 'agent') return <Navigate to="/dashboard/list" replace />;
  if (role === 'client') {
    if (!isPrimaryAccountHolder && !hasOrganizationMembership) {
      return <Navigate to="/dashboard/profile" replace />;
    }
    return <Navigate to="/dashboard/home" replace />;
  }
  if (role === 'candidate') return <Navigate to="/careers" replace />;
  return <Navigate to="/" replace />;
}



